/**
 * Sharing a finished run: the link, and the poster.
 *
 * Both carry the seed, because the point of a deterministic model is that a reader can
 * take what you sent them and check it rather than take your word for it.
 */

import { useEffect, useRef, useState } from 'react';
import type { RunLog } from 'walkout-engine';
import { canvasToBlob, drawPoster, posterFilename } from '../share/poster.ts';
import { posterUrl } from '../share/links.ts';
import './SharePanel.css';

type Copied = 'link' | 'image' | null;

export function SharePanel({ run, url }: { run: RunLog; url: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState<Copied>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (canvasRef.current) drawPoster(canvasRef.current, run, posterUrl(url));
  }, [run, url]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 2200);
    return () => clearTimeout(timer);
  }, [copied]);

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied('link');
    } catch {
      setProblem('The browser refused clipboard access. The link is in the address bar.');
    }
  };

  const copyImage = async (): Promise<void> => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const blob = await canvasToBlob(canvas);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied('image');
    } catch {
      // Firefox has no ClipboardItem image support at time of writing. Downloading is the
      // honest fallback rather than pretending it worked.
      setProblem('This browser will not copy images. Use download instead.');
    }
  };

  const download = async (): Promise<void> => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const blob = await canvasToBlob(canvas);
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = posterFilename(run);
    anchor.click();
    URL.revokeObjectURL(href);
  };

  return (
    <section className="share">
      <div className="share__poster">
        <canvas ref={canvasRef} className="share__canvas" />
      </div>

      <div className="share__actions">
        <p className="share__note">
          This link reproduces the run exactly — same seed, same settings, same day-by-day
          result. Nothing is stored anywhere; the whole run is in the address.
        </p>
        <div className="share__buttons">
          <button type="button" onClick={copyLink}>
            {copied === 'link' ? 'link copied' : 'copy link'}
          </button>
          <button type="button" onClick={copyImage}>
            {copied === 'image' ? 'image copied' : 'copy poster'}
          </button>
          <button type="button" onClick={download}>
            download poster
          </button>
        </div>
        {problem && <p className="share__problem">{problem}</p>}
      </div>
    </section>
  );
}
